/**
 * Softphone click-to-call (Phonomenal session-based dialer).
 *
 * Backend: POST {AI_PATH}/api/ai/voice/session-call  body { to }
 *   → looks up the CALLING agent's own Phonomenal member (by JWT userId),
 *     logs them into a session, and dials `to` — rings the agent's softphone
 *     first, then bridges the customer. (services/ai-service/routes.js)
 *
 * Two surfaces exported from here so the wiring lives in one place while we
 * finalise the backend:
 *   - useSoftphoneCall()  — hook: { placeCall, calling, toast, closeToast }
 *   - <SoftphoneButton />  — inline icon button that dials a KNOWN number
 *                            (used in the WhatsApp chat header).
 *   - <SoftphoneFab />     — global floating phone button: opens a dialog that
 *                            asks for the customer number (Cancel / Call).
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import { preopenFollowUpWa } from '../utils/whatsappFollowUp';
import {
  IconButton, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Snackbar, Tooltip, Fab, CircularProgress, Box, Typography, Collapse,
  Select, MenuItem, FormControl, InputLabel,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import PhoneIcon from '@material-ui/icons/Phone';
import HeadsetMicIcon from '@material-ui/icons/HeadsetMic';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import WhatshotIcon from '@material-ui/icons/Whatshot';
import InfoIcon from '@material-ui/icons/Info';
import AcUnitIcon from '@material-ui/icons/AcUnit';
import BlockIcon from '@material-ui/icons/Block';
import EventIcon from '@material-ui/icons/Event';
import SendIcon from '@material-ui/icons/Send';
import PhoneMissedIcon from '@material-ui/icons/PhoneMissed';
// Repeat team's own post-call form — used ONLY when <SoftphoneButton cohort />.
// Kept in its own file so the new-revenue taxonomy below stays untouched.
import CohortCallDisposition from './CohortCallDisposition';
import LeadScoreBar, { LEAD_SCORE_AUTO, scoreNeeded, scoreContradiction } from './LeadScoreBar';

const AI_PATH = (process.env.REACT_APP_AI_PATH || '').trim();
const API_PATH = (process.env.REACT_APP_PATH || '').trim();
const SOFTPHONE_URL = 'https://phonomenal.voizworks.com/phone';

function fmtDur(sec) {
  const s = Math.max(0, Math.floor(Number(sec) || 0));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
}

// The agent keeps the VOIZ softphone (SOFTPHONE_URL) logged in in its own tab.
// Once it's registered, a call dialled through our session-call API auto-rings
// that softphone — so the CRM button only has to PLACE the call. We DO gate on
// whether it's logged in and, if not, show a blocking dialog with a one-click
// "Open softphone" launcher (plus the URL for manual login).

// Keep 10-digit local / +91 forms as the backend already normalises to E.164
// (toE164India in phonomenalService). Just strip UI noise before sending.
const cleanNumber = (raw) => String(raw || '').replace(/[^\d+]/g, '');

export function useSoftphoneCall() {
  const [calling, setCalling] = useState(false);
  const [toast, setToast] = useState({ open: false, severity: 'success', message: '' });
  // Blocking "softphone offline / not provisioned" dialog.
  const [offline, setOffline] = useState({ open: false, reason: '' });
  // In-call popup. Opens once Phonomenal accepts the dial, so the agent has an
  // explicit End button instead of waiting for the call-end webhook to free
  // their line. Ending clears the Phonomenal call FIRST, then the caller shows
  // its response form — so filling the form never blocks the next call.
  // callId follows THIS call on VOIZ (GET /voice/live-call-status) so the popup
  // closes when the softphone hangs up; warning = VOIZ refused to drop the line.
  const [inCall, setInCall] = useState({ open: false, to: '', sessionId: null, callId: null, startedAt: null, warning: '' });
  const [ending, setEnding] = useState(false);
  // Post-call disposition form — shown after End on any call placed this way,
  // same wrap-up the progressive dialer already requires (Shailendra 2026-08-27).
  const [dispo, setDispo] = useState({ open: false, phone: '', talktime: 0, sessionId: null, unregisteredLeadLogsId: null });

  // Live "on call" timer, shown in the in-call dialog and sent to /voice/end-call
  // so the disposition form's duration + the CallReport it feeds are accurate.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!inCall.open) return undefined;
    const t = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [inCall.open]);
  const elapsed = inCall.startedAt ? Math.floor((Date.now() - inCall.startedAt) / 1000) : 0;

  const closeToast = useCallback(() => setToast((t) => ({ ...t, open: false })), []);
  const closeOffline = useCallback(() => setOffline({ open: false, reason: '' }), []);
  const closeDispo = useCallback(() => setDispo((d) => ({ ...d, open: false })), []);

  const placeCall = useCallback(async (rawNumber, customId) => {
    const to = cleanNumber(rawNumber);
    if (!to || to.replace(/\D/g, '').length < 10) {
      setToast({ open: true, severity: 'error', message: 'Enter a valid customer number' });
      return { success: false };
    }
    setCalling(true);
    try {
      // Pre-flight: is the agent's softphone logged in? If not, don't dial into
      // the void — pop the blocking "log in first" dialog.
      try {
        const st = await axios.get(`${AI_PATH}/api/ai/voice/softphone-status`);
        if (st.data?.success && st.data.provisioned && st.data.online === false) {
          setOffline({ open: true, reason: 'offline' });
          return { success: false, code: 'SOFTPHONE_OFFLINE' };
        }
        if (st.data?.success && st.data.provisioned === false) {
          setOffline({ open: true, reason: 'not_provisioned' });
          return { success: false, code: 'NOT_PROVISIONED' };
        }
      } catch (e) { /* status check failed — fall through and let the call attempt surface the error */ }

      // customId lets a caller (e.g. the Delivery Center chat's recording-request
      // lock) tag this specific call so a backend webhook can correlate it back to
      // the thing that triggered it — the backend defaults to crm-session-<ts> if
      // omitted, unchanged from before.
      const res = await axios.post(`${AI_PATH}/api/ai/voice/session-call`, { to, custom_id: customId || undefined });
      if (res.data?.success) {
        setToast({
          open: true, severity: 'success',
          message: `Calling ${to} — the call will ring on your softphone (ext ${res.data.extension || '—'})`,
        });
        setInCall({ open: true, to, sessionId: res.data.sessionId || null, callId: res.data.callId || null, startedAt: Date.now(), warning: '' });
      } else {
        setToast({ open: true, severity: 'error', message: res.data?.message || 'Call failed' });
      }
      return res.data || { success: false };
    } catch (err) {
      // Backend also guards (409 SOFTPHONE_OFFLINE) in case the status check was skipped.
      if (err.response?.data?.code === 'SOFTPHONE_OFFLINE') {
        setOffline({ open: true, reason: 'offline' });
      } else {
        setToast({ open: true, severity: 'error', message: err.response?.data?.message || err.message });
      }
      return { success: false };
    } finally {
      setCalling(false);
    }
  }, []);

  // End the live call. Clears Phonomenal FIRST so the agent's line is free
  // immediately, then resolves — the caller opens its response form after, so
  // filling the form never blocks the next dial. `onEnded` is where the caller
  // hooks its form modal.
  const endingRef = useRef(false);
  const endCall = useCallback(async (onEnded) => {
    if (endingRef.current) return;
    endingRef.current = true;
    setEnding(true);
    const talktime = inCall.startedAt ? Math.floor((Date.now() - inCall.startedAt) / 1000) : 0;
    let unregisteredLeadLogsId = null;
    try {
      const r = await axios.post(`${AI_PATH}/api/ai/voice/end-call`, {
        sessionId: inCall.sessionId, to: inCall.to, talktime,
      });
      // VOIZ still shows the call connected after dispose + session reset:
      // keep the popup open (it must match the softphone) with a warning. The
      // live-status poll below closes it the moment the call really ends.
      if (r.data?.lineStillLive) {
        setInCall((c) => ({ ...c, warning: r.data.message || 'VOIZ still shows this call connected — hang up on the softphone.' }));
        return;
      }
      unregisteredLeadLogsId = r.data?.unregisteredLeadLogsId || null;
    } catch (e) {
      // A dispose on a call that already ended 4xxs — the line is free either
      // way, which is all we need. Never block the agent on this.
      console.warn('[softphone] end-call:', e.response?.data?.message || e.message);
    } finally {
      endingRef.current = false;
      setEnding(false);
    }
    const ended = { to: inCall.to, sessionId: inCall.sessionId, talktime, unregisteredLeadLogsId };
    setInCall({ open: false, to: '', sessionId: null, callId: null, startedAt: null, warning: '' });
    // Open the wrap-up form — every call placed this way now gets the same
    // post-call disposition the progressive dialer requires.
    // sessionId travels with the disposition so the server can resolve the
    // real talktime for this exact call (the `talktime` above is wall-clock
    // since dialling, so it counts ring time on an unanswered call).
    if (inCall.to) setDispo({ open: true, phone: inCall.to, talktime, sessionId: inCall.sessionId, unregisteredLeadLogsId });
    if (typeof onEnded === 'function') onEnded(ended);
  }, [inCall.sessionId, inCall.to, inCall.startedAt]);

  // Follow the real call on VOIZ: when the buyer or the agent hangs up on the
  // softphone, close the popup and open the wrap-up form exactly as if End was
  // pressed — the CRM popup and the softphone must never disagree.
  const endCallRef = useRef(endCall);
  useEffect(() => { endCallRef.current = endCall; }, [endCall]);
  useEffect(() => {
    if (!inCall.open || !inCall.callId) return undefined;
    let stopped = false;
    const t = setInterval(async () => {
      if (stopped || endingRef.current) return;
      try {
        const r = await axios.get(`${AI_PATH}/api/ai/voice/live-call-status`, { params: { callId: inCall.callId } });
        if (!stopped && r.data?.ended) endCallRef.current();
      } catch (e) { /* transient — next tick retries */ }
    }, 3000);
    return () => { stopped = true; clearInterval(t); };
  }, [inCall.open, inCall.callId]);

  return {
    placeCall, calling, toast, closeToast, offline, closeOffline, inCall, endCall, ending,
    elapsed, dispo, closeDispo,
  };
}

// In-call WIDGET — a small floating, non-blocking card docked to the right
// edge of the screen instead of a modal, so the agent can keep browsing/using
// the CRM while the call is live (Shailendra 2026-08-29: the old modal dialog
// blanked out the whole page while a call was on). Pressing End disposes the
// call server-side, then hands control back so the caller can show its
// response form.
export function InCallDialog({ inCall, onEnd, ending, elapsed = 0 }) {
  if (!inCall?.open) return null;
  return (
    <Box style={{
      position: 'fixed', top: 84, right: 16, zIndex: 1450, width: 240,
      background: '#fff', borderRadius: 12, boxShadow: '0 6px 24px rgba(0,0,0,0.18)',
      border: '1px solid #e5e7eb', overflow: 'hidden',
    }}>
      <Box style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', background: '#e8f5e9' }}>
        <HeadsetMicIcon style={{ color: '#2e7d32', fontSize: 19 }} />
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Typography style={{ fontSize: 12, fontWeight: 800, color: '#1b5e20', lineHeight: 1.2 }}>
            On call · {fmtDur(elapsed)}
          </Typography>
          <Typography style={{ fontSize: 12.5, fontWeight: 700, color: '#1f2937', lineHeight: 1.2 }} noWrap>
            {inCall?.to}
          </Typography>
        </Box>
      </Box>
      <Box style={{ padding: '8px 10px' }}>
        <Button
          fullWidth size="small" onClick={onEnd} disabled={ending} variant="contained" color="error"
          style={{ fontWeight: 700, textTransform: 'none' }}
        >
          {ending ? 'Ending…' : 'End call'}
        </Button>
        {inCall?.warning && (
          <Typography style={{ fontSize: 11, fontWeight: 700, color: '#b71c1c', marginTop: 6, lineHeight: 1.3 }}>
            {inCall.warning}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

// Every <Select> inside CallDispositionDialog needs this — the dialog itself is forced to
// zIndex 1500 (to render above RtoRiskLockPopup's lock overlay, see that dialog's style prop
// below), but a Select's dropdown menu is its OWN separate MUI Modal that otherwise defaults to
// the theme's zIndex.modal (1300) — lower than its own parent dialog. Without this, the NI
// reason dropdown renders BEHIND the disposition form, unselectable (Shailendra 2026-09-11).
// Anchored below its own input so the list opens downward from the box.
const DISPOSITION_MENU_PROPS = {
  style: { zIndex: 1600 },
  anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
  transformOrigin: { vertical: 'top', horizontal: 'left' },
  getContentAnchorEl: null,
};

// The 5 fixed outcomes (Shailendra 2026-08-27, dates updated 2026-08-29) —
// replaces the old free-form Call Status dropdown + seller/product/city
// fields entirely. Only HOT and WARM require a future follow-up date/time
// (that's what gets the lead auto-queued again near that time via
// dialerReplenish.js) — COLD no longer asks for one. WON/DEAD take none.
// Backend: POST /api/ai/voice/call-outcome.
const OUTCOMES = [
  { value: 'WON',  label: 'Order Placed',                 sub: 'Logged as a completed sale — no follow-up scheduled.',        tag: 'WON',  dateMode: 'none',     Icon: CheckCircleIcon, color: '#1b5e20', bg: '#e8f5e9' },
  { value: 'HOT',  label: 'Interested - Followup — Date Confirmed', sub: "Buyer will be auto-dialled again at the time you set.",       tag: 'HOT',  dateMode: 'required', Icon: WhatshotIcon,    color: '#c62828', bg: '#fdecea' },
  { value: 'WARM', label: 'Call back - Details Shared',    sub: 'They asked for more info — schedule a callback.',            tag: 'WARM', dateMode: 'required', Icon: InfoIcon,        color: '#e65100', bg: '#fff4e5' },
  { value: 'COLD', label: 'Cx Want on COD - Advance Payment Issue', sub: "Wants COD, not ready to pay advance — no follow-up scheduled.",                 tag: 'COLD', dateMode: 'none',     Icon: AcUnitIcon,      color: '#1565c0', bg: '#e8f2fd' },
  { value: 'DEAD', label: 'Not Interested',                sub: 'No follow-up will be scheduled for this buyer.',             tag: 'DEAD', dateMode: 'none',     Icon: BlockIcon,       color: '#616161', bg: '#f5f5f5' },
  { value: 'SCOPE', label: 'Not Contacted',                 sub: "Call didn't lead to a real conversation.",         tag: '', dateMode: 'none', Icon: PhoneMissedIcon, color: '#78909c', bg: '#eceff1' },
];

// Client-side safety net for the shared productCategories dropdown (backed by
// a hand-edited StaticData doc) — always guarantee these are offered even if
// nobody's added them to that doc yet (Shailendra 2026-08-29).
const ENSURE_CATEGORIES = ['Diaper', 'No Gas Deo'];
// Fallback Product Category when nothing on the conversation tells us one —
// the agent can still change it since the field is unlocked in that case.
const DEFAULT_CATEGORY = 'No Gas Deo';

// Same reason list as the WhatsApp lead's own "Buyer Not Interested" 3-dot
// menu (MyChats.js) — choosing DEAD here goes through the exact same backend
// flow (markBuyerNotInterested: 75% monthly NI cap, Personal-Use routed to
// Neera instead of Shailendra), so the reason options must match exactly.
// Trimmed to three (Shailendra 2026-10-16) — Enquiry Only, Unreachable and
// Junk Lead dropped; the OTHERS outcome was removed from the form the same day.
const NI_REASONS = [
  'Not Interested — Rates Issue',
  'Not Interested — By Mistake Request',
  'Not Interested - Personal Use Only',
];

// Not Contacted is a single option now — no reason dropdown (Shailendra
// 2026-10-16). The backend still wants a reason, so send a fixed one that
// matches the old "Not Contacted - …" rows the reports already group on.
const SCOPE_REASON = 'Not Contacted - Ringing & Network Error';

/**
 * Post-call disposition form — shown after End on any call placed via
 * useSoftphoneCall (chat header call button, "Keep this lead" row, the
 * floating dialer, the Repeat drill-down's Call button). Posts to
 * POST /api/ai/voice/call-outcome, which ALSO writes a callreports row so
 * existing call-count/talk-time attendance reporting is unaffected.
 */
export function CallDispositionDialog({ dispo, onClose }) {
  const [outcome, setOutcome] = useState('');
  const [leadScore, setLeadScore] = useState(null);
  const [followUpAt, setFollowUpAt] = useState('');
  const [remark, setRemark] = useState('');
  const [niReason, setNiReason] = useState('');
  const [sellerName, setSellerName] = useState('');
  const [productCategory, setProductCategory] = useState('');
  const [categoryOptions, setCategoryOptions] = useState([]);
  // Locked = we already know it (Seller/Leads/conversation, or the AI-detected
  // product) — agent can't second-guess data we're confident about. Unlocked
  // = we have no idea, so it's optional and the agent can fill it in
  // (Shailendra 2026-08-29).
  const [nameLocked, setNameLocked] = useState(false);
  const [categoryLocked, setCategoryLocked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const lastPhoneRef = useRef('');

  useEffect(() => {
    const p10 = String(dispo?.phone || '').replace(/\D/g, '').slice(-10);
    if (dispo?.open && p10 && p10 !== lastPhoneRef.current) {
      lastPhoneRef.current = p10;
      setOutcome(''); setLeadScore(null); setFollowUpAt(''); setRemark(''); setNiReason('');
      setSellerName(''); setProductCategory(DEFAULT_CATEGORY); setCategoryOptions([]);
      setNameLocked(false); setCategoryLocked(false);

      // Auto-fill Name (Seller → Leads → WhatsApp conversation) + Product
      // Category (whatever the conversation already knows: AI-detected active
      // product, campaign guess, or FB ad name). Known values are LOCKED
      // (read-only); unknown ones stay editable, with category defaulting to
      // "No Gas Deo".
      axios.get(AI_PATH + '/api/ai/voice/call-context', { params: { phone: p10 } })
        .then((r) => {
          if (lastPhoneRef.current !== p10) return;
          if (r.data?.name) { setSellerName(r.data.name); setNameLocked(true); }
          if (r.data?.productCategory) { setProductCategory(r.data.productCategory); setCategoryLocked(true); }
        })
        .catch(() => {});
      axios.post(API_PATH + '/sales/callDropdowns?filterType=productCategories')
        .then((r) => {
          if (lastPhoneRef.current !== p10) return;
          const cats = r.data?.data || [];
          const merged = [...cats];
          ENSURE_CATEGORIES.forEach((c) => { if (!merged.includes(c)) merged.push(c); });
          setCategoryOptions(merged);
        })
        .catch(() => setCategoryOptions([...ENSURE_CATEGORIES]));
    }
  }, [dispo?.open, dispo?.phone]);

  const meta = OUTCOMES.find((o) => o.value === outcome) || null;
  const needsExtraReason = outcome === 'SCOPE';
  // Score is COMPULSORY for every outcome except SCOPE, and must not
  // contradict the outcome — both mirrored server-side in recordCallOutcome().
  const scoreMissing = scoreNeeded(outcome) && (leadScore === null || leadScore === undefined);
  const scoreClash = !!scoreContradiction(outcome, leadScore);
  const submitDisabled =
    submitting || !outcome ||
    (meta?.dateMode === 'required' && !followUpAt) ||
    (outcome === 'DEAD' && !niReason) ||
    scoreMissing || scoreClash;

  const submit = async () => {
    setSubmitting(true);
    // HOT / WARM → open WhatsApp to the buyer with the agent's intro pre-typed.
    const wa = preopenFollowUpWa(dispo?.phone, outcome === 'HOT' || outcome === 'WARM');
    try {
      await axios.post(AI_PATH + '/api/ai/voice/call-outcome', {
        phone: dispo?.phone,
        outcome,
        followUpAt: followUpAt ? new Date(followUpAt).toISOString() : undefined,
        remark: remark || undefined,
        notInterestedReason: outcome === 'DEAD' ? niReason : undefined,
        reason: needsExtraReason ? SCOPE_REASON : undefined,
        talktime: dispo?.talktime,
        unregisteredLeadLogsId: dispo?.unregisteredLeadLogsId,
        sellerName: sellerName || undefined,
        productCategory: productCategory || undefined,
        leadScore,
        leadScoreAuto: LEAD_SCORE_AUTO[outcome] ?? null,
      });
      wa.go();
      onClose();
    } catch (e) {
      wa.cancel();
      // A hit on the 75% monthly NI cap actually blocked the mark — the agent
      // needs to know, unlike other transient failures (the raw call is
      // already logged via end-call regardless, so those close quietly).
      if (e.response?.data?.code === 'NI_CAP_EXCEEDED') {
        alert(e.response.data.message);
      } else {
        onClose();
      }
    } finally { setSubmitting(false); }
  };

  const accent = meta?.color || '#1565c0';
  const needsDateNotSet = meta?.dateMode === 'required' && !followUpAt;

  return (
    <Dialog
      open={!!dispo?.open} onClose={() => {}} maxWidth="xs" fullWidth disableEscapeKeyDown
      // Explicit z-index above any full-screen lock overlay (e.g. RtoRiskLockPopup, 1300) —
      // this dialog MUST always be reachable once a call has ended, or the agent is stuck
      // unable to complete the wrap-up (seen live: recording-required lock's backdrop tied
      // this dialog's default MUI z-index of 1300 and won the paint order).
      style={{ zIndex: 1500 }}
      PaperProps={{ style: { borderRadius: 14, overflow: 'hidden' } }}
    >
      {/* Colour strip ties the whole dialog to whatever's selected — a quiet
          confirmation the agent picked the right thing, at a glance. */}
      <Box style={{ height: 5, background: accent, transition: 'background 150ms ease' }} />

      <Box style={{ padding: '18px 20px 4px' }}>
        <Typography style={{ fontSize: 11, fontWeight: 800, color: '#9ca3af', letterSpacing: 0.6 }}>
          CALL ENDED
        </Typography>
        <Box display="flex" alignItems="center" style={{ gap: 10, marginTop: 4 }}>
          <Typography style={{ fontSize: 20, fontWeight: 800, color: '#111827' }}>
            {dispo?.phone}
          </Typography>
          <Box style={{
            display: 'flex', alignItems: 'center', gap: 4, padding: '2px 9px', borderRadius: 999,
            background: '#f1f5f9', color: '#475569', fontSize: 12, fontWeight: 700,
          }}>
            <HeadsetMicIcon style={{ fontSize: 13 }} /> {fmtDur(dispo?.talktime)}
          </Box>
        </Box>
      </Box>

      <DialogContent style={{ paddingTop: 10 }}>
        {/* FIRST THING ON THE FORM (Shailendra 2026-10-21). It used to sit
            under the outcome cards and agents submitted straight past it —
            2,198 scores on day one, only 8% with the slider actually moved. */}
        <LeadScoreBar outcome={outcome} value={leadScore} onChange={setLeadScore} />
        <Box display="flex" style={{ gap: 8, marginBottom: 12 }}>
          <TextField
            fullWidth size="small" variant="outlined"
            label={nameLocked ? 'Name' : 'Name (optional)'}
            value={sellerName} onChange={(e) => setSellerName(e.target.value)}
            disabled={nameLocked}
          />
          <FormControl fullWidth size="small" variant="outlined" disabled={categoryLocked}>
            <InputLabel>{categoryLocked ? 'Product Category' : 'Product Category (optional)'}</InputLabel>
            <Select
              label={categoryLocked ? 'Product Category' : 'Product Category (optional)'}
              value={productCategory}
              onChange={(e) => setProductCategory(e.target.value)}
              MenuProps={DISPOSITION_MENU_PROPS}
            >
              {productCategory && !categoryOptions.includes(productCategory) && (
                <MenuItem value={productCategory}>{productCategory}</MenuItem>
              )}
              {categoryOptions.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </Select>
          </FormControl>
        </Box>

        <Typography style={{ fontSize: 11, fontWeight: 800, color: '#9ca3af', letterSpacing: 0.4, marginBottom: 8 }}>
          HOW DID IT GO?
        </Typography>
        {OUTCOMES.map((o) => {
          const selected = outcome === o.value;
          const { Icon } = o;
          return (
            <React.Fragment key={o.value}>
            <Box
              onClick={() => {
                // Seed the score bar from the outcome; the agent may drag it.
                setOutcome(o.value);
                setLeadScore(null); /* no seed: the agent must set the score themselves (Shailendra 2026-10-21) */
              }}
              style={{
                display: 'flex', alignItems: 'flex-start', gap: 12, padding: '11px 12px', marginBottom: 8,
                borderRadius: 10, cursor: 'pointer', position: 'relative',
                border: `1.5px solid ${selected ? o.color : '#e5e7eb'}`,
                background: selected ? o.bg : '#fff',
                boxShadow: selected ? `0 1px 0 ${o.color}22` : 'none',
                transition: 'border-color 120ms ease, background 120ms ease',
              }}
            >
              <Box style={{
                width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: selected ? o.color : '#f3f4f6',
                color: selected ? '#fff' : '#9ca3af',
                transition: 'background 120ms ease, color 120ms ease',
              }}>
                <Icon style={{ fontSize: 18 }} />
              </Box>
              <Box style={{ flex: 1, minWidth: 0 }}>
                <Typography style={{ fontSize: 13.5, fontWeight: 700, color: '#1f2937', lineHeight: 1.3 }}>
                  {o.label}{o.tag && <span style={{ color: o.color, fontWeight: 400 }}> {o.tag}</span>}
                </Typography>
                <Typography style={{ fontSize: 11.5, color: '#6b7280', lineHeight: 1.4, marginTop: 1 }}>
                  {o.sub}
                </Typography>
              </Box>
              {/* Selection dot — a real radio would need a whole FormControl;
                  this reads just as clearly and matches the card's own accent. */}
              <Box style={{
                width: 20, height: 20, borderRadius: '50%', flexShrink: 0, marginTop: 2,
                border: `2px solid ${selected ? o.color : '#d1d5db'}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'border-color 120ms ease',
              }}>
                {selected && <Box style={{ width: 10, height: 10, borderRadius: '50%', background: o.color }} />}
              </Box>
            </Box>
            {/* Date / reason opens right under the picked card, not at the
                bottom of the form (Shailendra 2026-10-16). */}
            {selected && (
              <Box style={{ marginTop: -4, marginBottom: 8 }}>
              <Collapse in={!!meta && meta.dateMode !== 'none'} timeout={180}>
                <Box style={{
                  marginTop: 4, marginBottom: 4, padding: '10px 12px', borderRadius: 10,
                  background: needsDateNotSet ? '#fff8e1' : '#fafafa',
                  border: `1px solid ${needsDateNotSet ? '#ffe082' : '#e5e7eb'}`,
                }}>
                  <Box display="flex" alignItems="center" style={{ gap: 6, marginBottom: 6 }}>
                    <EventIcon style={{ fontSize: 15, color: accent }} />
                    <Typography style={{ fontSize: 11.5, fontWeight: 700, color: '#374151' }}>
                      When should we call back? <span style={{ color: '#c62828' }}>*</span>
                    </Typography>
                  </Box>
                  <TextField
                    fullWidth size="small" type="datetime-local"
                    variant="outlined"
                    value={followUpAt} onChange={(e) => setFollowUpAt(e.target.value)}
                    inputProps={{ min: new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16) }}
                    style={{ background: '#fff' }}
                  />
                  <Typography style={{ fontSize: 10.5, color: '#9ca3af', marginTop: 5 }}>
                    This number is queued automatically around the time you set — no need to remember it yourself.
                  </Typography>
                </Box>
              </Collapse>

              {/* Same reason list + backend flow as the WhatsApp lead's own "Buyer Not
                  Interested" 3-dot menu — this marking carries the exact same weight
                  (75% monthly NI cap, Personal-Use routed to Neera). */}
              <Collapse in={outcome === 'DEAD'} timeout={180}>
                <Box style={{
                  marginTop: 4, marginBottom: 4, padding: '10px 12px', borderRadius: 10,
                  background: outcome === 'DEAD' && !niReason ? '#fff8e1' : '#fafafa',
                  border: `1px solid ${outcome === 'DEAD' && !niReason ? '#ffe082' : '#e5e7eb'}`,
                }}>
                  <Typography style={{ fontSize: 11.5, fontWeight: 700, color: '#374151', marginBottom: 6 }}>
                    Why isn't the buyer interested? <span style={{ color: '#c62828' }}>*</span>
                  </Typography>
                  <FormControl fullWidth size="small" variant="outlined" style={{ background: '#fff' }}>
                    <InputLabel>Reason</InputLabel>
                    <Select label="Reason" value={niReason} onChange={(e) => setNiReason(e.target.value)} MenuProps={DISPOSITION_MENU_PROPS}>
                      {NI_REASONS.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <Typography style={{ fontSize: 10.5, color: '#9ca3af', marginTop: 5 }}>
                    This marks the lead as lost and reassigns it, same as marking Not Interested from the chat itself.
                  </Typography>
                </Box>
              </Collapse>
              </Box>
            )}
            </React.Fragment>
          );
        })}

        {/* Skipped for DEAD/SCOPE — DEAD's reason dropdown already captures why. */}
        {outcome !== 'DEAD' && !needsExtraReason && (
          <TextField
            fullWidth size="small" label="Remarks (optional)" margin="dense" variant="outlined" multiline minRows={2}
            value={remark} onChange={(e) => setRemark(e.target.value)}
            style={{ marginTop: 6 }}
          />
        )}
      </DialogContent>

      <DialogActions style={{ padding: '4px 20px 18px', display: 'block' }}>
        <Button
          fullWidth variant="contained" disabled={submitDisabled} onClick={submit}
          startIcon={submitting ? <CircularProgress size={16} style={{ color: '#fff' }} /> : <SendIcon style={{ fontSize: 17 }} />}
          style={{
            background: submitDisabled ? '#e5e7eb' : accent, color: submitDisabled ? '#9ca3af' : '#fff',
            fontWeight: 800, padding: '11px 0', fontSize: 14.5, borderRadius: 10, boxShadow: 'none',
            textTransform: 'none',
          }}
        >
          {submitting ? 'Saving…' : outcome ? `Log as "${meta.label}"` : 'Pick an outcome to continue'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Launch the softphone in a new tab (reused by name so it isn't duplicated).
const openSoftphoneTab = () => window.open(SOFTPHONE_URL, 'voizSoftphone');

// Shared toast so both surfaces render the same feedback.
function CallToast({ toast, onClose }) {
  return (
    <Snackbar open={toast.open} autoHideDuration={6000} onClose={onClose}>
      <Alert severity={toast.severity} onClose={onClose}>{toast.message}</Alert>
    </Snackbar>
  );
}

// Blocking dialog shown when the agent's softphone isn't logged in (or not
// provisioned). Prominent, with a one-click launcher + the manual URL.
function SoftphoneOfflineDialog({ offline, onClose }) {
  const notProvisioned = offline.reason === 'not_provisioned';
  return (
    <Dialog open={offline.open} onClose={onClose} maxWidth="xs" fullWidth style={{ zIndex: 1500 }}>
      <DialogTitle style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <HeadsetMicIcon style={{ color: '#ed6c02' }} />
        {notProvisioned ? 'Softphone not set up' : 'Softphone not logged in'}
      </DialogTitle>
      <DialogContent>
        {notProvisioned ? (
          <div style={{ fontSize: 14, color: '#444', lineHeight: 1.5 }}>
            Your softphone isn’t provisioned yet. Ask your admin to add you under{' '}
            <b>Voice&nbsp;→&nbsp;Members</b>, then log in to the softphone before calling.
          </div>
        ) : (
          <>
            <div style={{ fontSize: 14, color: '#444', lineHeight: 1.5 }}>
              You need to be logged into the VOIZ softphone before you can place a call.
              Open it, log in, and keep the tab open — then press Call again.
            </div>
            <div style={{
              marginTop: 14, padding: '8px 10px', background: '#f5f5f5', borderRadius: 6,
              fontFamily: 'monospace', fontSize: 12, color: '#333', wordBreak: 'break-all',
            }}>
              {SOFTPHONE_URL}
            </div>
            <div style={{ fontSize: 12, color: '#888', marginTop: 6 }}>
              Use the button below, or open this URL manually in a new tab.
            </div>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {!notProvisioned && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<OpenInNewIcon />}
            onClick={() => { openSoftphoneTab(); onClose(); }}
          >
            Open softphone
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

/**
 * Inline call button for a chat/lead header — dials a number we already know.
 * Props: number (string, required), title?, color? (icon color).
 *
 * `cohort` (default FALSE) swaps ONLY the post-call form for the repeat team's
 * own one (CohortCallDisposition). Opt-in by design: this component is shared
 * with MyChats, where new-revenue agents must keep the existing
 * WON/HOT/WARM/COLD/DEAD form untouched (Shailendra 2026-09-04). A repeat
 * buyer's real answer — "old stock", "your price moved" — has no home in that
 * taxonomy, which is why cohort agents were falling back to the legacy
 * free-text form and losing every call-back date.
 */
// `disabled` + `disabledReason` (2026-10-16): the caller can veto a dial and
// say why — used by the cohort drill-down's pacing gate ("called yesterday, no
// answer", "already called twice today"). The reason replaces the tooltip, so
// the agent sees the rule rather than a dead button.
export function SoftphoneButton({ number, title = 'Call on softphone', color = '#fff', size = 'small', onDispositionClosed, onCallPlaced, cohort = false, name, customId, disabled = false, disabledReason }) {
  const { placeCall, calling, toast, closeToast, offline, closeOffline, inCall, endCall, ending, elapsed, dispo, closeDispo } = useSoftphoneCall();
  const handleDispoClose = () => {
    closeDispo();
    if (typeof onDispositionClosed === 'function') onDispositionClosed();
  };
  const handleClick = async () => {
    const res = await placeCall(number, customId);
    // Fires the instant the dial API call succeeds — before any webhook/poll round-trip —
    // so a caller (e.g. the recording-required lock) can open its UI immediately on "agent
    // started dialling" rather than waiting to hear back from the backend.
    if (res?.success && typeof onCallPlaced === 'function') onCallPlaced(res);
  };
  return (
    <>
      <Tooltip title={!number ? 'No number on this chat' : (disabled ? (disabledReason || 'Calling is paused for this buyer') : title)}>
        <span>
          <IconButton
            size={size}
            disabled={calling || !number || disabled}
            onClick={handleClick}
            style={{ color: disabled ? '#c7cdd4' : color }}
          >
            {calling ? <CircularProgress size={18} style={{ color }} /> : <PhoneIcon fontSize="small" />}
          </IconButton>
        </span>
      </Tooltip>
      <CallToast toast={toast} onClose={closeToast} />
      <SoftphoneOfflineDialog offline={offline} onClose={closeOffline} />
      <InCallDialog inCall={inCall} onEnd={() => endCall()} ending={ending} elapsed={elapsed} />
      {cohort
        ? <CohortCallDisposition dispo={{ ...dispo, name }} onClose={handleDispoClose} />
        : <CallDispositionDialog dispo={dispo} onClose={handleDispoClose} />}
    </>
  );
}

/**
 * Global floating phone button. Click → dialog asks for the customer number,
 * Cancel / Call. Mount once in the main layout so it floats on every page.
 */
export function SoftphoneFab() {
  const { placeCall, calling, toast, closeToast, offline, closeOffline, inCall, endCall, ending, elapsed, dispo, closeDispo } = useSoftphoneCall();
  const [open, setOpen] = useState(false);
  const [number, setNumber] = useState('');

  const handleCall = async () => {
    const res = await placeCall(number);
    if (res?.success) {
      setOpen(false);
      setNumber('');
    }
  };

  return (
    <>
      <Fab
        color="primary"
        aria-label="call"
        onClick={() => setOpen(true)}
        style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1200, background: '#25D366' }}
      >
        <PhoneIcon />
      </Fab>

      <Dialog open={open} onClose={() => !calling && setOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Call a customer</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Customer number"
            placeholder="e.g. 8826345311"
            value={number}
            onChange={(e) => setNumber(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !calling) handleCall(); }}
            margin="dense"
          />
          <div style={{ fontSize: 12, color: '#888', marginTop: 8 }}>
            Keep your softphone logged in — the call will ring there when you press Call.
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={calling}>Cancel</Button>
          <Button
            onClick={handleCall}
            disabled={calling}
            variant="contained"
            color="primary"
            startIcon={calling ? <CircularProgress size={16} color="inherit" /> : <PhoneIcon />}
          >
            {calling ? 'Calling…' : 'Call'}
          </Button>
        </DialogActions>
      </Dialog>

      <CallToast toast={toast} onClose={closeToast} />
      <SoftphoneOfflineDialog offline={offline} onClose={closeOffline} />
      <InCallDialog inCall={inCall} onEnd={() => endCall()} ending={ending} elapsed={elapsed} />
      <CallDispositionDialog dispo={dispo} onClose={closeDispo} />
    </>
  );
}

export default SoftphoneFab;
